import {
  CalendarDays,
  ChevronRight,
  Clock,
  Coffee,
  Info,
  type LucideIcon,
  Moon,
  Sun,
} from 'lucide-react'
import { type FormEvent, useEffect, useId, useMemo, useState } from 'react'
import {
  formatSnoozeLabel,
  parseCustom,
  type SnoozeParseError,
  type SnoozePresetId,
  snoozePresets,
  toDatetimeLocal,
} from '../../lib/snooze-times'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'

const ICONS: Record<SnoozePresetId, LucideIcon> = {
  tonight: Moon,
  tomorrow: Sun,
  weekend: Coffee,
  nextWeek: CalendarDays,
}
const ERRORS: Record<SnoozeParseError, string> = {
  past: 'Bitte einen Zeitpunkt in der Zukunft wählen.',
  too_far: 'Höchstens ein Jahr im Voraus.',
  invalid: 'Bitte Datum und Uhrzeit vollständig angeben.',
}
const MAX_AHEAD_DAYS = 365
// The picker shows wall time without a zone; say so where it is not German time.
const foreignZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone !== 'Europe/Berlin'
  } catch {
    return false
  }
})()

export function SnoozeSheet({
  open,
  subject,
  now = () => new Date(),
  onClose,
  onPick,
}: {
  open: boolean
  subject: string | null
  /** Injectable clock; presets are computed once per opening. */
  now?: () => Date
  onClose: () => void
  onPick: (until: Date, label: string) => void
}) {
  const [customOpen, setCustomOpen] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<SnoozeParseError | null>(null)
  const inputId = useId()
  const errorId = useId()
  // biome-ignore lint/correctness/useExhaustiveDependencies: recompute per opening only
  const openedAt = useMemo(() => now(), [open])
  const presets = useMemo(() => snoozePresets(openedAt), [openedAt])

  useEffect(() => {
    if (!open) return
    setCustomOpen(false)
    setError(null)
    setValue(toDatetimeLocal(presets[1]?.until ?? openedAt))
  }, [open, presets, openedAt])

  const pick = (until: Date) => onPick(until, formatSnoozeLabel(until, now()))
  const submitCustom = (e: FormEvent) => {
    e.preventDefault()
    const result = parseCustom(value, now())
    if (!result.ok) {
      setError(result.error)
      return
    }
    pick(result.until)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Später erinnern"
      description={`„${subject?.trim() || '(kein Betreff)'}“ verschwindet bis dahin aus dem Posteingang`}
      footer={
        <p className="w-full text-xs leading-relaxed text-text-tertiary">
          Ein Tipp genügt – danach „Rückgängig“ in der Leiste.
          <br />
          Wischen ←: kurz = Später + Papierkorb, ganz durch = Papierkorb · → bleibt Erledigt
        </p>
      }
    >
      <div className="flex flex-col gap-2.5 pt-1">
        <div className="grid grid-cols-2 gap-2">
          {presets.map((preset) => {
            const Icon = ICONS[preset.id]
            return (
              <button
                key={preset.id}
                type="button"
                disabled={preset.disabled}
                aria-disabled={preset.disabled}
                onClick={() => pick(preset.until)}
                className="flex min-h-11 flex-col items-start gap-1.5 rounded-xl border border-border-default bg-bg-overlay/60 p-3 text-left transition-colors hover:border-info/60 hover:bg-info/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/50 disabled:pointer-events-none disabled:opacity-40"
              >
                <Icon className="h-5 w-5 text-info" aria-hidden="true" />
                <span className="text-sm font-medium text-text-primary">{preset.title}</span>
                <span className="text-xs text-text-tertiary">{preset.label}</span>
              </button>
            )
          })}
        </div>
        <button
          type="button"
          aria-expanded={customOpen}
          onClick={() => setCustomOpen(!customOpen)}
          className="flex min-h-12 items-center gap-2.5 rounded-lg border border-border-default px-3 text-left text-sm text-text-primary transition-colors hover:bg-bg-overlay focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/50"
        >
          <Clock className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden="true" />
          <span className="flex-1">Datum und Uhrzeit wählen …</span>
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-text-tertiary transition-transform ${customOpen ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </button>
        {customOpen && (
          <form onSubmit={submitCustom} className="flex flex-col gap-1.5" noValidate>
            <label htmlFor={inputId} className="sr-only">
              Datum und Uhrzeit
            </label>
            <div className="flex gap-2">
              <Input
                id={inputId}
                type="datetime-local"
                value={value}
                min={toDatetimeLocal(openedAt)}
                max={toDatetimeLocal(new Date(openedAt.getTime() + MAX_AHEAD_DAYS * 86_400_000))}
                aria-invalid={error !== null}
                aria-describedby={error ? errorId : undefined}
                onChange={(e) => {
                  setValue(e.target.value)
                  setError(null)
                }}
                className="min-w-0 flex-1"
              />
              <Button type="submit" size="md">
                Übernehmen
              </Button>
            </div>
            {foreignZone && (
              <p className="text-xs text-text-tertiary">Uhrzeit in deutscher Zeit (Berlin).</p>
            )}
            {error && (
              <p id={errorId} role="alert" className="text-xs text-danger">
                {ERRORS[error]}
              </p>
            )}
          </form>
        )}
        <p className="flex gap-2 text-xs leading-relaxed text-text-tertiary">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Liegt so lange im Ordner „Später“ (auch auf dem Server). Zur Zeit kommt sie oben in den
            Posteingang zurück, als ungelesen – ohne extra Push.
          </span>
        </p>
      </div>
    </Dialog>
  )
}
